import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { Box, Icon, Stack, Text, Flex } from '@tonic-ui/react';
import {
  DropdownMenu,
  FieldTextLabel,
  FormContainer,
  InputField,
  supportFormValidation,
  TextareaField,
  Tags,
  SubmitButton,
} from '../../components/forms';
import { getC1UserDetails } from '../../store';
import { getAllTimezones } from '../../utils/timezone';
import { useAlert } from '../../components/alert';

type SupportAuthenticatedFormProps = {
  onSubmit: (supportData: SupportRequestData) => Promise<void>;
};

const SupportFormFields = (): JSX.Element => {
  const { t } = useTranslation('', { keyPrefix: 'support.supportPage' });
  const { isValid, dirty, errors } = useFormikContext<SupportFormValues>();
  const timeZones: { name: string; id: string }[] = getAllTimezones();
  const { showWarning, closeWarning } = useAlert();

  const issueTypeList = [
    {
      id: 'subscription',
      name: t('caseTypeMap.subscription'),
    },
    {
      id: 'user-management',
      name: t('caseTypeMap.usermanagement'),
    },
    {
      id: 'threat',
      name: t('caseTypeMap.threat'),
    },
    {
      id: 'technical',
      name: t('caseTypeMap.technical'),
    },
  ];

  const severityLevelList = [
    {
      id: 'p4',
      name: t('severityLevelList.general'),
    },
    {
      id: 'p3',
      name: t('severityLevelList.low'),
    },
    {
      id: 'p2',
      name: t('severityLevelList.medium'),
    },
    {
      id: 'p1',
      name: t('severityLevelList.high'),
    },
  ];

  useEffect(() => {
    if (errors.issueDescription === 'validations.supportFormProblemDescription') {
      showWarning(t('supportFormProblemDescription'));
    } else {
      closeWarning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <>
      <Flex
        direction="column"
        flex="3"
        paddingTop="5x"
        paddingLeft="5x"
        height="100%"
        maxWidth="450px"
      >
        <Stack spacing="4x" marginBottom="5x">
          <Text size="2xl">{t('pageTitle')}</Text>
          <Text size="md">{t('pageDescription')}</Text>
        </Stack>
        <Stack spacing="5x">
          <InputField
            id="name"
            type="text"
            name="name"
            label={t('nameTextLabel') as string}
            isRequired={true}
          />
          <InputField
            id="company-name"
            type="text"
            name="companyName"
            label={t('companyNameTextLabel') as string}
            isRequired={true}
          />
          <Box>
            <FieldTextLabel
              name={t('emailTextLabel') as string}
              isRequired={true}
              htmlFor="support-form-tags-field"
            />
            <Tags name="emails" placeholder="someone@example.com" id="support-form-tags-field" />
          </Box>
          <DropdownMenu
            name="issueType"
            id="support-form-case-type-menu-selection"
            options={issueTypeList}
            label={t('caseTypeTextLabel')}
            placeholder={t('select')}
            isRequired={true}
            maxHeight={150}
          />
          <DropdownMenu
            name="severity"
            id="support-form-severity-menu-selection"
            options={severityLevelList}
            label={t('severityTextLabel')}
            placeholder={t('select')}
            isRequired={true}
            maxHeight={150}
          />
          <Stack>
            <TextareaField
              name="issueDescription"
              id="support-form-problem-description-field"
              label={t('problemDescriptionTextLabel') as string}
              isRequired={true}
              rows={3}
              cols={12}
            />
          </Stack>
        </Stack>
        <Stack
          id="phoneDetailsBox"
          data-id="phone-details-box"
          direction="row"
          spacing="6x"
          mt="5x"
        >
          <Box flex="1">
            <InputField
              id="phone-number"
              data-id="support-form-phone-field"
              type="text"
              name="phoneNumber"
              placeholder={t('phoneNumberPlaceholder') as string}
              label={t('phoneNumberTextLabel') as string}
              isRequired={true}
            />
          </Box>
          <Box maxWidth="112px">
            <InputField
              id="phone-number-ext"
              data-id="support-form-phone-ext-field"
              type="text"
              name="phoneNumberExt"
              label={t('phoneNumberExtTextLabel') as string}
            />
          </Box>
        </Stack>
        <Box mt="3x">
          <DropdownMenu
            name="timezone"
            id="support-form-timeZone-menu-selection"
            options={timeZones}
            label={t('timeZoneTextLabel')}
            placeholder={t('select')}
            isRequired={true}
            maxHeight={150}
          />
        </Box>
        <Flex py="4x" justifyContent="flex-end">
          <SubmitButton
            variant="primary"
            id="support-form-submit"
            label={t('submitButtonText')}
            disabled={!(isValid && dirty)}
            width="160px"
          />
        </Flex>
      </Flex>
    </>
  );
};

const SupportAuthenticatedForm = ({ onSubmit }: SupportAuthenticatedFormProps): JSX.Element => {
  const { userEmail, userName, userTimeZone } = useSelector(
    (state) => ({
      userEmail: (getC1UserDetails(state, 'email') ??
        getC1UserDetails(state, 'wsEmailAddress')) as string,
      userName: getC1UserDetails(state, 'name') as string,
      userTimeZone: (getC1UserDetails(state, 'timezone') ??
        getC1UserDetails(state, 'wsTimeZone')) as string,
    }),
    shallowEqual
  );

  const initialValues: SupportFormValues = {
    name: userName,
    emails: [userEmail],
    companyName: '',
    issueType: '',
    severity: '',
    issueDescription: '',
    phoneNumber: '',
    phoneNumberExt: '',
    timezone: userTimeZone,
  };

  return (
    <FormContainer
      name="supportFormAuthGovCloud"
      id="support-form-authenticated-govcloud"
      data-id="support-form-authenticated-govcloud"
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize={true}
      initialValues={initialValues}
      customValidation={supportFormValidation}
      submitAction={(formValues) =>
        onSubmit({
          name: formValues.name,
          emails: formValues.emails,
          companyName: formValues.companyName,
          issueType: formValues.issueType,
          severity: formValues.severity,
          issueDescription: formValues.issueDescription,
          timezone: formValues.timezone,
          service: ['threat', 'technical'].includes(`${formValues?.issueType}`)
            ? 'workloadSecurity'
            : '',
          phone:
            formValues.phoneNumber +
            (formValues.phoneNumberExt ? ` ext. ${formValues.phoneNumberExt}` : ''),
        } as SupportRequestData)
      }
    >
      <SupportFormFields />
    </FormContainer>
  );
};

SupportAuthenticatedForm.displayName = 'SupportAuthenticatedForm';

export default SupportAuthenticatedForm;
